import { useMutation, useQuery, useQueryClient } from "react-query";
import useClientApi from "../api-services/client";

const CLIENTS_KEY = "clients";
const CLIENTS_LOCATION_KEY = "clients";
const TRADING_PARTNER_CLIENTS_KEY = "trading-partner-clients";


// Hook for SUPER_ADMIN to get all clients
const useGetAllClients = (params = {}, options = {}) => {
  const clientApi = useClientApi();

  return useQuery({
    queryKey: [CLIENTS_KEY, params],
    queryFn: () => clientApi.getAllClients(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    ...options,
  });
};


// Hook for TRADING_PARTNER to get their accessible clients
const useGetAllClientsForTP = (params = {}, options = {}) => {
  const clientApi = useClientApi();

  return useQuery({
    queryKey: [TRADING_PARTNER_CLIENTS_KEY, params],
    queryFn: () => clientApi.getAllClientsForTP(params),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    ...options,
  });
};


// Hook for TRADING_PARTNER to switch context to a specific client
// This mutation should be called when a trading partner selects a different client
const useSwitchContextTradingPartner = (options = {}) => {
  const queryClient = useQueryClient();
  const clientApi = useClientApi();

  return useMutation({
    mutationFn: (clientId) => clientApi.switchContextTradingPartner({ clientId }),
    onSuccess: (data) => {
      // Invalidate both client lists to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: [TRADING_PARTNER_CLIENTS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CLIENTS_KEY],
      });

      options.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('Failed to switch trading partner context:', error);
      options.onError?.(error);
    },
    ...options,
  });
};


// Generic hook that returns the appropriate client list based on user role
const useGetClientsForRole = (role, params = {}, options = {}) => {
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isTradingPartner = role === 'TRADING_PARTNER';

  const superAdminQuery = useGetAllClients(params, {
    ...options,
    enabled: isSuperAdmin && (options.enabled !== false),
  });

  const tradingPartnerQuery = useGetAllClientsForTP(params, {
    ...options,
    enabled: isTradingPartner && (options.enabled !== false),
  });

  // Return the appropriate query based on role
  if (isSuperAdmin) {
    return superAdminQuery;
  } else if (isTradingPartner) {
    return tradingPartnerQuery;
  }

  // Return empty query for other roles
  return {
    data: null,
    isLoading: false,
    isError: false,
    error: null,
  };
};


const useGetClientLocations = (tenantId, options = {}) => {
	const clientApi = useClientApi();

	return useQuery({
		queryKey: [CLIENTS_LOCATION_KEY, tenantId],
		queryFn: () => clientApi.getAllClientLocations(tenantId),
		staleTime: 5 * 60 * 1000, // 5 minutes
		refetchOnWindowFocus: false,
		...options,
	});
};


export {
  useGetAllClients,
  useGetAllClientsForTP,
  useGetClientLocations,
  useSwitchContextTradingPartner,
  useGetClientsForRole,
  CLIENTS_KEY,
  TRADING_PARTNER_CLIENTS_KEY,
};